import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, removeItem } = useCart();
  const { user } = useAuth();
  const [pricing, setPricing] = useState({ lines: [], subtotal: 0 });
  const [discount, setDiscount] = useState({ code: "", percent: 0 });
  const [designFee, setDesignFee] = useState(false);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState({});
  const [shipQuote, setShipQuote] = useState({ self: {items:[], shipping:0}, printify: {items:[], shipping:0}, shipping_total: 0, shipping_label: "" });
  const [codeInput, setCodeInput] = useState("");

  useEffect(() => {
    (async () => {
      if (items.length === 0) { setPricing({ lines: [], subtotal: 0 }); return; }
      const cartItems = items.map(({product_id,color,size,qty})=>({product_id,color,size,qty}));
      const r = await api.post("/cart/price", cartItems);
      setPricing(r.data);
      // Fetch any product details we don't already have (read latest via functional setter).
      const uniqueIds = [...new Set(items.map(i => i.product_id))];
      setProducts(prev => {
        const missing = uniqueIds.filter(id => !prev[id]);
        if (missing.length === 0) return prev;
        // Fire fetches in background; when they resolve we merge results in.
        (async () => {
          const results = await Promise.all(missing.map(async id => {
            try { const pr = await api.get(`/products/${id}`); return [id, pr.data.product]; }
            catch { return null; }
          }));
          const map = {};
          for (const r of results) if (r) map[r[0]] = r[1];
          if (Object.keys(map).length) setProducts(p => ({ ...p, ...map }));
        })();
        return prev;
      });
      // Hybrid split shipping quote (SELF + PRINTIFY grouped)
      try {
        const q = await api.post("/cart/shipping-quote", cartItems);
        setShipQuote(q.data);
      } catch {}
    })();
  }, [items]);

  const applyDiscount = async () => {
    try {
      const r = await api.post("/discount/validate", { code: codeInput });
      setDiscount({ code: r.data.code, percent: r.data.percent });
      toast.success(`${r.data.percent}% off applied`);
    } catch { toast.error("Invalid code"); }
  };

  const discountAmt = (pricing.subtotal * discount.percent) / 100;
  const feeAmt = designFee ? 5 : 0;
  const shippingTotal = shipQuote.shipping_total || 0;
  const total = Math.max(0, pricing.subtotal - discountAmt + shippingTotal + feeAmt);

  const checkout = async () => {
    if (items.length === 0) return;
    setLoading(true);
    try {
      const r = await api.post("/checkout/session", {
        items: items.map(({product_id,color,size,qty})=>({product_id,color,size,qty})),
        origin_url: window.location.origin,
        add_design_fee: designFee,
        email: user?.email || "",
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error("Checkout error");
      setLoading(false);
    }
  };

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center" data-testid="cart-empty">
        <h1 className="font-display text-5xl text-[color:var(--hb-navy)]">Your bag is empty</h1>
        <p className="text-sm text-neutral-600 mt-4">Discover our latest custom apparel.</p>
        <Link to="/collections/graphics-official-collection" className="hb-btn-primary mt-8 inline-block">Shop the Graphics Official Collection</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 sm:px-8 py-12" data-testid="cart-page">
      <h1 className="font-display text-5xl text-[color:var(--hb-navy)] mb-8">Your Bag</h1>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-6">
          {items.map((it, i) => {
            const key = `${it.product_id}-${it.color}-${it.size}`;
            const p = products[it.product_id];
            const line = pricing.lines[i];
            const mockup = p?.mockups?.[it.color]?.front;
            return (
              <div key={key} className="flex gap-4 border-b border-neutral-200 pb-6" data-testid={`cart-line-${i}`}>
                <div className="w-28 h-28 bg-[color:var(--hb-warm)] shrink-0">
                  {mockup && <img src={fileUrl(mockup)} alt="" className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1">
                  <div className="text-sm font-medium">{p?.name || "Product"}</div>
                  <div className="text-xs text-neutral-500 mt-1">{it.color} · Size {it.size}</div>
                  <div className="mt-2 text-sm">${line?.unit?.toFixed(2)}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex items-center border border-neutral-300">
                      <button onClick={()=>updateQty(key, it.qty-1)} className="px-2 py-1" data-testid={`qty-dec-${i}`}>-</button>
                      <span className="px-3 text-sm">{it.qty}</span>
                      <button onClick={()=>updateQty(key, it.qty+1)} className="px-2 py-1" data-testid={`qty-inc-${i}`}>+</button>
                    </div>
                    <button onClick={()=>removeItem(key)} className="text-xs text-neutral-500 hover:text-[color:var(--hb-red)]" data-testid={`remove-${i}`}>Remove</button>
                  </div>
                </div>
                <div className="text-sm font-medium">${line?.line_total?.toFixed(2)}</div>
              </div>
            );
          })}
        </div>

        <aside className="bg-white border border-neutral-200 p-6 h-fit" data-testid="cart-summary">
          <h3 className="font-display text-2xl mb-4">Summary</h3>
          <div className="flex justify-between text-sm py-1"><span>Subtotal</span><span>${pricing.subtotal.toFixed(2)}</span></div>
          {/* Split shipping breakdown */}
          {(shipQuote.self?.items?.length > 0 || shipQuote.printify?.items?.length > 0) ? (
            <div className="py-2 border-t border-neutral-200 mt-2" data-testid="shipping-breakdown">
              <div className="text-[10px] tracking-widest uppercase text-neutral-500 mb-1">Shipping</div>
              {shipQuote.self?.items?.length > 0 && (
                <div className="flex justify-between text-xs py-0.5" data-testid="shipping-self-row">
                  <span>In-house ({shipQuote.self.items.reduce((s,i)=>s+i.qty,0)} items)</span>
                  <span>{shipQuote.self.shipping === 0 ? "Free" : `$${shipQuote.self.shipping.toFixed(2)}`}</span>
                </div>
              )}
              {shipQuote.printify?.items?.length > 0 && (
                <div className="flex justify-between text-xs py-0.5" data-testid="shipping-printify-row">
                  <span>Printify POD ({shipQuote.printify.items.reduce((s,i)=>s+i.qty,0)} items)</span>
                  <span>${shipQuote.printify.shipping.toFixed(2)}</span>
                </div>
              )}
              {shipQuote.self?.items?.length > 0 && shipQuote.printify?.items?.length > 0 && (
                <p className="text-[10px] text-neutral-500 mt-1.5 italic">You'll receive two shipments with separate tracking.</p>
              )}
            </div>
          ) : (
            <div className="flex justify-between text-sm py-1"><span>Shipping</span><span>${shippingTotal.toFixed(2)}</span></div>
          )}
          {discount.percent > 0 && (
            <div className="flex justify-between text-sm py-1 text-[color:var(--hb-royal)]"><span>Discount ({discount.code})</span><span>-${discountAmt.toFixed(2)}</span></div>
          )}
          <label className="flex items-center gap-2 text-xs mt-3 py-2 border-t border-neutral-200 mt-4 pt-3">
            <input type="checkbox" checked={designFee} onChange={e=>setDesignFee(e.target.checked)} data-testid="design-fee-checkbox"/>
            Add Custom Design service (+$5.00)
          </label>
          <div className="flex gap-2 mt-4">
            <input value={codeInput} onChange={e=>setCodeInput(e.target.value)} placeholder="Discount code" className="flex-1 border border-neutral-300 px-2 py-1.5 text-sm" data-testid="discount-input"/>
            <button onClick={applyDiscount} className="text-xs uppercase tracking-widest border border-neutral-300 px-3" data-testid="apply-discount">Apply</button>
          </div>
          <div className="flex justify-between text-lg font-medium mt-6 pt-4 border-t border-neutral-200">
            <span>Total</span><span data-testid="cart-total">${total.toFixed(2)}</span>
          </div>
          <button onClick={checkout} disabled={loading} className="hb-btn-primary w-full mt-6" data-testid="checkout-btn">
            {loading ? "Redirecting…" : "Secure Checkout"}
          </button>
          <p className="text-[10px] text-neutral-500 mt-2 text-center">Powered by Stripe · SSL secured</p>
        </aside>
      </div>
    </div>
  );
}
